/**
 * Default V2 configuration. Used when validating partial server config so the
 * widget always has a full V2Configuration. Shape matches agent-desktop
 * getDefaultV2Configuration and V2ChatConfiguration.
 *
 * Two factory functions exist on purpose:
 * - getDefaultServerV2(): Full defaults when the entire server.v2 key is missing.
 *   Used as initial state and validator fallback. All nested keys are populated.
 * - getDefaultV2Configuration(): Sparse defaults for validators when merging partial
 *   server config. Sub-validators deep-merge with this; empty objects (e.g. colors: {})
 *   mean "no override". Critical values (e.g. percentageRollout) are aligned with
 *   getDefaultServerV2() so behavior is consistent whether v2 is absent or partial.
 */

import { ALIGNMENTS, INITIAL_BUTTON_TYPES } from '@/domain/configuration';
import type { V2Configuration } from '@/domain/configurationV2';

/** Default z-index for the chat widget (Panel, Fullscreen, WidgetContainer). */
export const DEFAULT_Z_INDEX = 2147483000;

/** Default V2 appearance colors; single source of truth for App theme fallbacks and getDefaultServerV2. */
export const DEFAULT_V2_COLORS = {
  accent: '#009b00',
  alert: '#FE1A20',
  attachmentBackground: '#312E2E',
  brandPrimary: '#f3f3f3',
  brandSecondary: '#F3F3F3',
  chatButtonIcon: '#FFFFFF',
  chatLauncher: '#009B00',
  greetingCloseButton: '#000000',
  headerBackgroundColor: '#009B00',
  productImageBackgroundColor: '#ffffff',
} as const;

/** Default V2 font; single source of truth for App theme fallbacks and getDefaultServerV2. */
export const DEFAULT_V2_FONT = "'Helvetica Neue', Helvetica, sans-serif";

/**
 * Fallbacks for the surfaces drawn on the brand's own page — the Chat Bar, the
 * chat icon button, and the launchers. Needed because validV2Appearance merges
 * the sparse defaults below, leaving colors, fonts and border.radius absent for
 * an org that omits them. They deliberately do not match DEFAULT_V2_COLORS: a
 * visitor sees the on-page surfaces side by side, so they match each other, and
 * an org that configures no accent gets a purple launcher opening a green panel.
 */
export const DEFAULT_ON_PAGE_ACCENT = '#8C69F0';
export const DEFAULT_ON_PAGE_BORDER_RADIUS_PX = '24px';

/**
 * Default server.v2 used when the v2 key is missing from server config.
 * Shared by configSliceInit (initial state) and validServerConfiguration (fallback).
 */
export function getDefaultServerV2(): V2Configuration {
  return {
    appearance: {
      alignment: ALIGNMENTS.RIGHT,
      border: {
        color: '',
        radius: 'NONE',
        width: '0px',
      },
      colors: {
        ...DEFAULT_V2_COLORS,
      },
      dropShadow: true,
      fonts: {
        primary: DEFAULT_V2_FONT,
        secondary: DEFAULT_V2_FONT,
      },
      format: {
        date: 'MM/DD/YYYY',
        time: 'h:mm a',
      },
      header: {
        backgroundColor: '',
        fontColor: '',
        headingText: '',
        logo: '',
        subheadingText: '',
      },
      input: {
        chatWindow: { placeholderText: 'Send message' },
        placeholderTexts: ['Send message'],
      },
      launcherButton: {
        // No `useLogo` key on purpose: deepMerge skips undefined, so an org that
        // omits it reaches resolveLauncherButton unset and keeps whatever its skin
        // drew before the field existed. Setting it here would be a silent opt-out
        // for every Enhanced org with a header logo.
        text: 'Live chat',
        type: INITIAL_BUTTON_TYPES.ICON,
      },
      messages: {
        chatClosed: {
          message:
            "We want to chat with you, but it's outside of our office hours. Chat again tomorrow to pick up where we left off.",
          title: 'Whoops, sorry we missed you!',
        },
        chatThrottled: {
          message:
            'We want to chat with you, but we are experiencing higher chat volumes than usual. Please visit our contact page for other ways to reach out to us.',
          title: 'Whoops, sorry we missed you!',
        },
        consumerMessageBackgroundColor: '#F3F3F3',
        typingIndicatorText: 'Agent is typing...',
      },
      privacyDisclaimer: {
        disclaimerBody: '',
        isActivated: false,
      },
      showPoweredByGladly: false,
      zIndex: DEFAULT_Z_INDEX,
    },
    behavior: {
      attachmentsEnabled: true,
      displayRules: {
        rules: [],
        showByDefault: true,
      },
      lng: 'en-us',
      throttling: {
        autoThrottlingEnabled: false,
        autoThrottlingThreshold: 1800,
        percentageRollout: 0,
      },
    },
    endChatMessages: {
      agentEnded:
        '<div>Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.</div>',
      consumerNoReply:
        '<div>We haven&#39;t heard from you in a while, <<USERNAME>>. Type another message to pick up where we left off.</div>',
      newSessionStarted:
        '<div>We noticed you have another active chat. You can send us a message to chat here, and we will end the other chat for you</div>',
      timeExpired:
        '<div>Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.</div>',
    },
    tooltips: {
      attachmentIcon: 'Attach file',
      chatButton: 'Open Chat',
      closeWidget: 'Hide Chat bar',
      closeWindow: 'Hide Chat window',
      sendIcon: 'Send',
    },
  };
}

/**
 * Sparse defaults for V2 sub-validators. Used when deep-merging partial server
 * config; empty nested objects mean "no override". format.date defaults to
 * 'MM/DD/YYYY' here; legacy theme.formats.date uses null for "use browser locale".
 */
export function getDefaultV2Configuration(): V2Configuration {
  return {
    appearance: {
      alignment: 'RIGHT',
      border: {
        color: '#FFFFFF',
        width: '1px',
      },
      colors: {},
      dropShadow: true,
      fonts: {},
      format: {
        date: 'MM/DD/YYYY',
        time: 'h:mm a',
      },
      header: {
        backgroundColor: '',
        fontColor: '',
        headingText: '',
        logo: '',
        subheadingText: '',
      },
      input: {
        chatWindow: { placeholderText: 'Send message' },
        placeholderTexts: ['Send message'],
      },
      launcherButton: {
        // Still no `useLogo` key — see getDefaultServerV2 above.
        text: 'Live chat',
        type: INITIAL_BUTTON_TYPES.ICON,
      },
      messages: {
        chatClosed: {
          message:
            "We want to chat with you, but it's outside of our office hours. Chat again tomorrow to pick up where we left off.",
          title: 'Whoops, sorry we missed you!',
        },
        chatThrottled: {
          message:
            'We want to chat with you, but we are experiencing higher chat volumes than usual. Please visit our contact page for other ways to reach out to us.',
          title: 'Whoops, sorry we missed you!',
        },
        consumerMessageBackgroundColor: '#F3F3F3',
        typingIndicatorText: 'Agent is typing...',
      },
      privacyDisclaimer: {
        disclaimerBody: '',
        isActivated: false,
      },
      showPoweredByGladly: false,
      zIndex: DEFAULT_Z_INDEX,
    },
    behavior: {
      attachmentsEnabled: true,
      displayRules: {
        rules: [],
        showByDefault: true,
      },
      lng: 'en-us',
      throttling: {
        autoThrottlingEnabled: false,
        autoThrottlingThreshold: 1800,
        percentageRollout: 0,
      },
    },
    endChatMessages: {
      agentEnded:
        '<div>Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.</div>',
      consumerNoReply:
        '<div>We haven&#39;t heard from you in a while, <<USERNAME>>. Type another message to pick up where we left off.</div>',
      newSessionStarted:
        '<div>We noticed you have another active chat. You can send us a message to chat here, and we will end the other chat for you</div>',
      timeExpired:
        '<div>Thanks for chatting <<USERNAME>>! More questions? Chat again to pick up where we left off.</div>',
    },
    tooltips: {
      attachmentIcon: 'Attach file',
      chatButton: 'Open Chat',
      closeWidget: 'Hide Chat bar',
      closeWindow: 'Hide Chat window',
      sendIcon: 'Send',
    },
  };
}
